import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import SEOHead from 'components/SEOHead';
import { analyticsService } from 'services/analytics/analyticsService';
import { fetchBeerPacks } from 'services/firebase/FetchBeerPack';
import { saveMailingUnsubscribeRequest } from 'services/firebase/mailingUnsubscribeService';
import { BeerPack } from 'utils/types/BeerPack';

const PROMO_CODE = 'QUERO2GARRAFAS';
const PROMO_CODE_DISPLAY = '#QUERO2GARRAFAS';
const PACK_QUANTITY = 4;

const findPackByQuantity = (
  packs: BeerPack[],
  quantity: number,
): BeerPack | undefined =>
  packs.find(pack => pack.label.includes(String(quantity)));

const MailingUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSelectedPlan, setUsedReferralCode } = useSubscription();

  const nameFromUrl =
    searchParams.get('nome')?.trim() || searchParams.get('name')?.trim() || '';
  const emailFromUrl = searchParams.get('email')?.trim() || '';

  const displayName = nameFromUrl || 'amigo(a)';

  const [email, setEmail] = useState(emailFromUrl);
  const [unsubscribeError, setUnsubscribeError] = useState('');
  const [unsubscribeSuccess, setUnsubscribeSuccess] = useState('');
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  useEffect(() => {
    analyticsService.trackPageView('/descadastro', 'Descadastro de Mailing');
  }, []);

  const validateEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleUnsubscribe = useCallback(async () => {
    setUnsubscribeError('');
    setUnsubscribeSuccess('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setUnsubscribeError('Por favor, informe seu e-mail.');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setUnsubscribeError('Por favor, informe um e-mail válido.');
      return;
    }

    setIsUnsubscribing(true);
    try {
      await saveMailingUnsubscribeRequest({
        email: trimmedEmail,
        name: nameFromUrl || undefined,
        source: 'mailing_unsubscribe_page',
        pageUrl: window.location.pathname + window.location.search,
      });
      setUnsubscribeSuccess(
        'Recebemos sua solicitação! Em breve removeremos seu e-mail da nossa lista.',
      );
      setEmail('');
    } catch (error) {
      console.error('Erro ao solicitar descadastro:', error);
      setUnsubscribeError(
        'Não foi possível enviar sua solicitação. Tente novamente.',
      );
    } finally {
      setIsUnsubscribing(false);
    }
  }, [email, nameFromUrl]);

  const handleSubscribe = useCallback(async () => {
    setSubscribeError('');
    setIsSubscribing(true);

    try {
      const packs = await fetchBeerPacks();
      const pack = findPackByQuantity(packs, PACK_QUANTITY);

      if (!pack) {
        setSubscribeError(
          'Plano indisponível no momento. Tente novamente em instantes.',
        );
        return;
      }

      setSelectedPlan({
        type: '',
        price: pack.priceWithoutCup.discounted,
        originalPrice: pack.priceWithoutCup.original,
        label: pack.label,
        quantity: 1,
        total: parseFloat(pack.priceWithoutCup.discounted),
        copo: false,
        pack: pack.label,
        subscriptionID: '',
      });

      setUsedReferralCode(PROMO_CODE);

      analyticsService.trackSubscriptionFlow(
        'shipping_options',
        pack.label,
        parseFloat(pack.priceWithoutCup.discounted),
      );

      navigate('/subscription');
    } catch (error) {
      console.error('Erro ao iniciar assinatura:', error);
      setSubscribeError(
        'Não foi possível iniciar a assinatura. Tente novamente.',
      );
    } finally {
      setIsSubscribing(false);
    }
  }, [navigate, setSelectedPlan, setUsedReferralCode]);

  return (
    <Box sx={{ minHeight: '60vh', bgcolor: '#faf7f3' }}>
      <SEOHead
        title="Descadastro | Clube da 42"
        description="Solicite a remoção do seu e-mail da nossa lista de mensagens."
      />

      {/*
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f98f08 0%, #e67a00 100%)',
          color: '#fff',
          py: { xs: 5, md: 6 },
          px: 2,
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              mb: 2,
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            Sentiremos sua falta!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              lineHeight: 1.8,
              opacity: 0.95,
              fontSize: { xs: '1rem', md: '1.1rem' },
            }}
          ></Typography>
        </Container>
      </Box>
      */}

      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 5 } }}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '2px solid #f98f08',
            mb: 4,
          }}
        >
          <Box
            sx={{
              bgcolor: '#f98f08',
              color: '#fff',
              px: { xs: 3, md: 4 },
              py: 2,
            }}
          >
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.5, opacity: 0.9 }}
            >
              Oferta exclusiva
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              Mas antes, para mostrar que queremos muito você conosco, temos uma
              promoção exclusiva para você, {displayName}.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="body1"
              sx={{ lineHeight: 1.85, color: 'text.primary', mb: 3 }}
            >
              Te oferecemos{' '}
              <Box
                component="strong"
                sx={{
                  color: '#c96c1e',
                  fontWeight: 700,
                  fontSize: { xs: '1.35rem', sm: '1.5rem' },
                }}
              >
                2 garrafas extras
              </Box>{' '}
              por mês durante{' '}
              <Box
                component="strong"
                sx={{
                  color: '#c96c1e',
                  fontWeight: 700,
                  fontSize: { xs: '1.35rem', sm: '1.5rem' },
                }}
              >
                6 meses
              </Box>
              . Ou seja, você assina 4 garrafas e recebe 6. O que acha?
            </Typography>

            <Typography variant="body2" sx={{ color: '#6b5c4a', mb: 1 }}>
              Basta adicionar o cupom na etapa de pagamento:
            </Typography>

            <Box
              sx={{
                bgcolor: '#1a1008',
                color: '#fff',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                mb: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 700, letterSpacing: 1 }}
              >
                {PROMO_CODE_DISPLAY}
              </Typography>
            </Box>

            {subscribeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {subscribeError}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubscribe}
              disabled={isSubscribing}
              sx={{
                bgcolor: '#c96c1e',
                color: '#fff',
                borderRadius: '12px',
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#a85818', boxShadow: 'none' },
              }}
            >
              {isSubscribing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Assinar Agora'
              )}
            </Button>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
                color: '#6b5c4a',
              }}
            >
              Plano de 4 garrafas/mês
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{ color: '#6b5c4a', px: 2, fontStyle: 'italic' }}
          >
            ou
          </Typography>
        </Divider>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {unsubscribeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {unsubscribeError}
              </Alert>
            )}
            {unsubscribeSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {unsubscribeSuccess}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Seu e-mail"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isUnsubscribing || !!unsubscribeSuccess}
              sx={{ mb: 2 }}
            />

            <Button
              fullWidth
              variant="outlined"
              onClick={handleUnsubscribe}
              disabled={isUnsubscribing || !!unsubscribeSuccess}
              sx={{
                borderColor: '#6b5c4a',
                color: '#6b5c4a',
                borderRadius: '12px',
                py: 1.25,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#4a3f32',
                  color: '#4a3f32',
                  bgcolor: 'rgba(0,0,0,0.02)',
                },
              }}
            >
              {isUnsubscribing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Remover meu e-mail'
              )}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default MailingUnsubscribe;
